import type { Popup } from '@shady-pines/db';
import { isNonNil, now, type Uuid } from '@shady-pines/types';
import { supabaseApi } from '../api/api-store';
import type { PopupState } from './types';

export type PopupApi = ReturnType<typeof PopupApi>;

export const PopupApi = (
  get: () => PopupState,
  set: (partial: Partial<PopupState> | ((state: PopupState) => Partial<PopupState>)) => void,
) =>
  ({
    fetchActivePopups: async (currentTimestamp: string) => {
      const loading = get().loading.popups;
      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              popups: true,
            },
          }) as const satisfies Partial<PopupState>,
      );

      const { data: popups } = await supabaseApi()
        .from('popups')
        .select('*')
        .is('deleted_at', null)
        .gt('expires_at', currentTimestamp)
        .lt('active_at', currentTimestamp)
        .order('expires_at', { ascending: true });

      set(
        (s) =>
          ({
            popups: popups ?? [],
            lastPopupRefresh: now(),
            loading: {
              ...s.loading,
              popups: false,
            },
          }) as const satisfies Partial<PopupState>,
      );
    },
    fetchAllPopups: async () => {
      const loading = get().loading.popups;
      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              popups: true,
            },
          }) as const satisfies Partial<PopupState>,
      );

      const { data: popups } = await supabaseApi()
        .from('popups')
        .select('*')
        .is('deleted_at', null)
        .order('expires_at', { ascending: true });

      set(
        (s) =>
          ({
            popups: popups ?? [],
            lastPopupRefresh: now(),
            loading: {
              ...s.loading,
              popups: false,
            },
          }) as const satisfies Partial<PopupState>,
      );
    },
    fetchPopupById: async (id: Uuid<Popup>) => {
      const loading = get().loading.priority;
      const existing = get().popups?.find((p) => p.id === id);
      if (isNonNil(existing)) {
        return;
      }
      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              priority: true,
            },
          }) as const satisfies Partial<PopupState>,
      );
      const { data: popup } = await supabaseApi().from('popups').select('*').eq('id', id).single();

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              priority: false,
            },
            priorityPopup: popup,
          }) as const satisfies Partial<PopupState>,
      );
    },
  }) as const;
